import {
  IonButton,
  IonCol,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonMenu,
  IonRow,
  useIonToast,
} from '@ionic/react';
import { appsOutline, chevronForwardOutline, settingsOutline } from 'ionicons/icons';
import { useRef, useState } from 'react';
import IOSInstallSheet from '../IOSInstallSheet/IOSInstallSheet';
import { getRoutes } from '../../config/config';
import MenuAvatar from './MenuAvatar';
import MenuListSection from './MenuListSection';
import MenuUserInfo from './MenuUserInfo';
import CopyRight from '../CopyRight';
import MenuNotAuthenticated from './MenuNotAuthenticated';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxCommandCreator } from 'appRedux/actions';
import { IState, IUserInfo } from 'appRedux/createStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDice, faIndustry, faSplotch } from '@fortawesome/free-solid-svg-icons';
import { AuthService } from 'services/auth';
import { take, tap } from 'rxjs';
import { selectAuthStateTransformed } from 'appRedux/selectors';
import { toastConfig, ToastType } from '../../config/ToastConfig';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { casinoBgMap } from 'components/CasinoCard.tsx/CasinoCard';
import { casinoIdListEnum } from 'common/commonEnum';
import { UNIVERSAL_SEARCH_ROUTE, isSlotCheckSearchEnabled } from 'lib/slotCheckSearchAccess';
import { useSlotCheckSearchAccessRefresh } from 'hooks/useSlotCheckSearchAccessRefresh';
import { canCreateCasinoAccount } from 'pages/CasinoAccounts/createCasinoAccountAccess';
import { canAccessDeleteCasinoAccounts } from 'pages/DeleteCasinoAccounts/deleteCasinoAccountsAccess';
import {
  shouldShowCloakEgmsMenuItem,
  shouldShowEgmsMenuCategory,
  shouldShowGameNamesManagementMenuItem,
  shouldShowManufacturersManagementMenuItem,
} from 'pages/ManufacturersManagement/egmsMenuAccess';
const addCasinoIcon = 'assets/icon/menuicons/casino.svg';
// Footer Casinos tab buildings glyph (tab-location-icon), currentColor for menu tinting.
const selectCasinoIcon = 'assets/icon/menuicons/select-casino.svg';
const slotCheckSearchIcon = 'assets/icon/menuicons/slot-check-search.svg';
const adminDashboardIcon = 'assets/icon/menuicons/adminDash.svg';
const analyticsDashIcon = 'assets/icon/menuicons/analyticsDash.svg';
const reportsIcon = 'assets/icon/menuicons/reports.svg';
const egmsIcon = 'assets/icon/menuicons/egms.svg';
const playerEngagementIcon = 'assets/icon/menuicons/player-engagement.svg';
const slotCheckEngagementIcon = 'assets/icon/menuicons/slot-check-engagement.svg';
const addToHomeIcon = 'assets/icon/menuicons/addtohome.svg';

const SideMenu = () => {
  const dispatch = useDispatch();
  const commands = ReduxCommandCreator(dispatch);
  const history = useHistory();
  const authService = new AuthService();
  const authState = useSelector(selectAuthStateTransformed);
  const [presentToast] = useIonToast();
  const location = useLocation();

  // Slot Check Search menu item: users.slotCheckSearchEnabled = 1 / "1" only.
  const canSlotCheckSearch = useSelector<IState>((state) =>
    isSlotCheckSearchEnabled(state.app.persistedState.userInfo?.slotCheckSearchEnabled)
  ) as boolean;
  // Fresh users.slotCheckSearchEnabled (not only the value cached at login).
  const refreshSlotCheckSearchAccess = useSlotCheckSearchAccessRefresh(!!authState);
  const isSlotcheckInternal = useSelector<IState>((state) =>
    state.app.persistedState.userInfo?.groups?.includes('SlotcheckInternal')
  );
  const isCasinoAdmin = useSelector<IState>(
    (state) =>
      state.app.persistedState.userInfo?.groups?.includes('LOTCAdmin') ||
      state.app.persistedState.userInfo?.groups?.includes('EurekaAdmin')
  );
  const hasCreateCasinoAccount = useSelector<IState>((state) =>
    canCreateCasinoAccount(state.app.persistedState.userInfo?.groups || [])
  );
  const hasEnterpriseReporting = useSelector<IState>((state) =>
    state.app.persistedState.userInfo?.groups?.includes('slotcheck-enterprise-reporting')
  );
  const userGroups =
    (useSelector<IState>((state) => state.app.persistedState.userInfo?.groups) as string[]) ||
    [];
  const hasCloakTheEgms = shouldShowCloakEgmsMenuItem(userGroups);
  const hasManufacturersManagement = shouldShowManufacturersManagementMenuItem(userGroups);
  const hasGameNamesManagement = shouldShowGameNamesManagementMenuItem(userGroups);
  const hasEgmsMenuCategory = shouldShowEgmsMenuCategory(userGroups);
  const hasEmulatePlayers = useSelector<IState>((state) =>
    state.app.persistedState.userInfo?.groups?.includes('slotcheck-enterprise-emulatePlayers')
  );
  const hasDeleteCasinoAccounts = useSelector<IState>((state) =>
    canAccessDeleteCasinoAccounts(state.app.persistedState.userInfo?.groups || [])
  );
  const hasSystemDashboard = useSelector<IState>((state) =>
    state.app.persistedState.userInfo?.groups?.includes('slotcheck-enterprise-systemDashboard')
  );
  const { aliasInitial2, aliasName, avatarId, avatarBgId } = useSelector<IState>(
    (state) => state.app.persistedState.userInfo
  ) as IUserInfo;

  const selectedCasinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number | undefined;

  const sideHeaderBg = (() => {
    const img = selectedCasinoId !== undefined
      ? casinoBgMap[selectedCasinoId as casinoIdListEnum]
      : undefined;
    const src = img ? `/${img}` : '/assets/images/bg-choose-casino.png';
    return `url('${src}') center / cover no-repeat`;
  })();

  const sideMenuRef = useRef<HTMLIonMenuElement | null>(null);
  const [menuAnimKey, setMenuAnimKey] = useState(0);

  const signOut = () => {
    history.push('/logout');
  };

  const signIn = async () => {
    await sideMenuRef.current?.toggle();
  };

  const handleCloseMenu = async () => {
    await sideMenuRef?.current?.close();
  };

  const showToast = ({ type = 'success', message, cssClass }: { type?: ToastType; message: string; cssClass?: string }) => {
    toastConfig.color = type;
    toastConfig['message'] = message;
    void presentToast({ ...toastConfig, cssClass });
  };

  const handleResetTour = async () => {
    await authService
      .UpdateMe({ hasCompletedOnboarding: false })
      .pipe(
        take(1),
        tap(async () => {
          await commands.SetTourState(false);
          await handleCloseMenu();

          if (history.location.pathname !== '/tabs/home') {
            history.push('/tabs/home');
          } else {
            history.go(0);
          }
        })
      )
      .toPromise();
  };

  const goBackAction = () => {
    handleCloseMenu();
    history.push('/tabs/home');
  };


  const { showInstallOption, hasNativePrompt, triggerInstall, isIOSSafari, isChromeIOS } = useInstallPrompt();
  const [showIOSSheet, setShowIOSSheet] = useState(false);

  const menuRoutes = getRoutes();

  return (
    <>
      <IonMenu
        type="overlay"
        side="end"
        contentId="main"
        className="bg-menu"
        swipeGesture={false}
        menuId="main"
        onIonWillOpen={() => {
          setMenuAnimKey(k => k + 1);
          refreshSlotCheckSearchAccess();
        }}
        ref={sideMenuRef}
      >
        <div
          key={menuAnimKey}
          className="sidemenu-container"
          style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}
        >
          <div className="side-header" style={{ background: sideHeaderBg }}>
            <div className="sidemenu-header">
              {!authState && <MenuNotAuthenticated signIn={signIn} />}
              {authState && (
                <>
                  <MenuAvatar
                    aliasInitial={aliasInitial2}
                    avatarId={avatarId}
                    avatarBgId={avatarBgId}
                  />
                  <MenuUserInfo
                    aliasName={aliasName}
                    onProfileClick={() => {
                      handleCloseMenu().catch(() => null);
                      history.push('/profile');
                    }}
                  />
                </>
              )}
            </div>
          </div>

          <div
            className="menu-container"
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <IonContent className="menu-content" style={{ flex: 1, minHeight: 0 }}>

              {/* Select Casino for every authenticated account (all roles). forceSelect
                  prevents last-viewed auto-open from bouncing the user back in. */}
              {authState && (
                <IonList lines="none" className="menu-list">
                  <IonItem
                    routerLink="/auth/choosecasinos?forceSelect=1"
                    onClick={handleCloseMenu}
                    className={`action-container${
                      location.pathname.startsWith('/auth/choosecasinos') ||
                      location.pathname.startsWith('/tabs/casinos')
                        ? ' sidemenu-item-active'
                        : ''
                    }`}
                    detail={false}
                  >
                    <IonLabel>
                      <IonIcon icon={selectCasinoIcon} className="sidemenu-icon" />
                      Select Casino
                    </IonLabel>
                  </IonItem>
                  {canSlotCheckSearch && (
                    <IonItem
                      routerLink={UNIVERSAL_SEARCH_ROUTE}
                      onClick={handleCloseMenu}
                      className={`action-container${
                        location.pathname.startsWith(UNIVERSAL_SEARCH_ROUTE) ? ' sidemenu-item-active' : ''
                      }`}
                      detail={false}
                    >
                      <IonLabel>
                        <IonIcon icon={slotCheckSearchIcon} className="sidemenu-icon" />
                        Slot Check Search
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>
              )}

              {hasEgmsMenuCategory && (
                <IonList lines="none" className="menu-list">
                  <IonItem
                    className="action-container sidemenu-egms-parent"
                    lines="none"
                  >
                    <IonLabel>
                      <IonIcon icon={egmsIcon} className="sidemenu-icon" />
                      EGMs
                    </IonLabel>
                  </IonItem>
                  {hasCloakTheEgms && (
                    <IonItem
                      routerLink="/cloak-egms"
                      onClick={handleCloseMenu}
                      className={`action-container sidemenu-egms-child${location.pathname.startsWith('/cloak-egms') ? ' sidemenu-item-active' : ''}`}
                      detail={false}
                    >
                      <IonLabel>
                        <IonIcon icon={playerEngagementIcon} className="sidemenu-icon" />
                        Cloak EGMs
                      </IonLabel>
                    </IonItem>
                  )}
                  {hasManufacturersManagement && (
                    <IonItem
                      routerLink="/manufacturers-management"
                      onClick={handleCloseMenu}
                      className={`action-container sidemenu-egms-child${location.pathname.startsWith('/manufacturers-management') ? ' sidemenu-item-active' : ''}`}
                      detail={false}
                    >
                      <IonLabel>
                        <FontAwesomeIcon
                          className="sidemenu-icon fa"
                          icon={faIndustry}
                        />
                        Manufacturers Management
                      </IonLabel>
                    </IonItem>
                  )}
                  {hasGameNamesManagement && (
                    <IonItem
                      routerLink="/game-names-management"
                      onClick={handleCloseMenu}
                      className={`action-container sidemenu-egms-child${location.pathname.startsWith('/game-names-management') ? ' sidemenu-item-active' : ''}`}
                      detail={false}
                    >
                      <IonLabel>
                        <FontAwesomeIcon
                          className="sidemenu-icon fa"
                          icon={faDice}
                        />
                        Game Names Management
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>
              )}

              {hasEmulatePlayers && (
                <IonList lines="none" className="menu-list">
                  <IonItem
                    routerLink="/emulate-players"
                    onClick={handleCloseMenu}
                    className={`action-container${location.pathname.startsWith('/emulate-players') ? ' sidemenu-item-active' : ''}`}
                    detail={false}
                  >
                    <IonLabel>
                      <IonIcon icon={playerEngagementIcon} className="sidemenu-icon" />
                      Emulate Players
                    </IonLabel>
                  </IonItem>
                </IonList>
              )}

              {hasDeleteCasinoAccounts && (
                <IonList lines="none" className="menu-list">
                  <IonItem
                    routerLink="/delete-casino-accounts"
                    onClick={handleCloseMenu}
                    className={`action-container${location.pathname.startsWith('/delete-casino-accounts') ? ' sidemenu-item-active' : ''}`}
                    detail={false}
                  >
                    <IonLabel>
                      <IonIcon icon={playerEngagementIcon} className="sidemenu-icon" />
                      Delete Casino Accounts
                    </IonLabel>
                  </IonItem>
                </IonList>
              )}

              {(hasEnterpriseReporting || hasSystemDashboard) && (
                <IonList lines="none" className="menu-list">
                  <IonItem
                    className="action-container sidemenu-reports-parent"
                    lines="none"
                  >
                    <IonLabel>
                      <IonIcon icon={reportsIcon} className="sidemenu-icon" />
                      Reports
                    </IonLabel>
                  </IonItem>
                  {hasEnterpriseReporting && (
                    <>
                      <IonItem
                        routerLink="/reports/player-engagement"
                        onClick={handleCloseMenu}
                        className={`action-container sidemenu-reports-child${location.pathname.startsWith('/reports/player-engagement') ? ' sidemenu-item-active' : ''}`}
                        detail={false}
                      >
                        <IonLabel>
                          <IonIcon icon={playerEngagementIcon} className="sidemenu-icon" />
                          Player Engagement
                        </IonLabel>
                      </IonItem>
                      <IonItem
                        routerLink="/reports/slot-check-engagement"
                        onClick={handleCloseMenu}
                        className={`action-container sidemenu-reports-child${location.pathname.startsWith('/reports/slot-check-engagement') ? ' sidemenu-item-active' : ''}`}
                        detail={false}
                      >
                        <IonLabel>
                          <IonIcon icon={slotCheckEngagementIcon} className="sidemenu-icon" />
                          Slot Check Engagement
                        </IonLabel>
                      </IonItem>
                      <IonItem
                        routerLink="/reports/player-development"
                        onClick={handleCloseMenu}
                        className={`action-container sidemenu-reports-child${location.pathname.startsWith('/reports/player-development') ? ' sidemenu-item-active' : ''}`}
                        detail={false}
                      >
                        <IonLabel>
                          <IonIcon icon={playerEngagementIcon} className="sidemenu-icon" />
                          Player Development
                        </IonLabel>
                      </IonItem>
                    </>
                  )}
                  {hasSystemDashboard && (
                    <IonItem
                      routerLink="/system-dashboard"
                      onClick={handleCloseMenu}
                      className={`action-container sidemenu-reports-child${location.pathname.startsWith('/system-dashboard') ? ' sidemenu-item-active' : ''}`}
                      detail={false}
                    >
                      <IonLabel>
                        <IonIcon icon={playerEngagementIcon} className="sidemenu-icon" />
                        System Dashboard
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>
              )}

              {(isSlotcheckInternal || isCasinoAdmin) && (
                <IonList lines="none" className="menu-list">
                  {isSlotcheckInternal && (
                    <IonItem
                      routerLink="/admin/dashboard"
                      onClick={handleCloseMenu}
                      className="action-container"
                    >
                      <IonLabel>
                        <FontAwesomeIcon
                          className="sidemenu-icon fa"
                          icon={faSplotch}
                          color="gold"
                        />
                        Admin Dashboard
                      </IonLabel>
                    </IonItem>
                  )}

                  {isSlotcheckInternal && (
                    <IonItem
                      routerLink="/admin/analytics"
                      onClick={handleCloseMenu}
                      className="action-container"
                    >
                      <IonLabel>
                        <IonIcon icon={analyticsDashIcon} className="sidemenu-icon" />
                        Analytics Dashboard
                      </IonLabel>
                    </IonItem>
                  )}

                  {isCasinoAdmin && (
                    <IonItem
                      routerLink="/admin/casino"
                      onClick={handleCloseMenu}
                      className="action-container"
                    >
                      <IonLabel>
                        <IonIcon icon={adminDashboardIcon} className="sidemenu-icon" />
                        Casino Admin
                      </IonLabel>
                    </IonItem>
                  )}


                  {hasCreateCasinoAccount && (
                    <IonItem
                      routerLink="/admin/addaccount"
                      onClick={handleCloseMenu}
                      className="action-container"
                    >
                      <IonLabel>
                        <IonIcon icon={addCasinoIcon} className="sidemenu-icon" />
                        Add Casino Account
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>
              )}

              {authState && (
                <IonList lines="none" className="menu-list user-pages">
                  <IonItem
                    routerLink="/settings"
                    onClick={handleCloseMenu}
                    className={`action-container${location.pathname.startsWith('/settings') ? ' sidemenu-item-active' : ''}`}
                    detail={false}
                  >
                    <IonLabel>
                      <IonIcon icon={settingsOutline} className="sidemenu-icon" />
                      <span className="sidemenu-text">Settings</span>
                    </IonLabel>
                  </IonItem>
                  <IonItem
                    routerLink="/other"
                    onClick={handleCloseMenu}
                    className={`action-container${location.pathname.startsWith('/other') || location.pathname.startsWith('/support-center') || location.pathname.startsWith('/definitions') ? ' sidemenu-item-active' : ''}`}
                    detail={false}
                  >
                    <IonLabel>
                      <IonIcon icon={appsOutline} className="sidemenu-icon" />
                      <span className="sidemenu-text">Other</span>
                    </IonLabel>
                    <IonIcon icon={chevronForwardOutline} slot="end" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '16px' }} />
                  </IonItem>
                </IonList>
              )}

              {(showInstallOption || isIOSSafari || isChromeIOS) && (
                <IonList lines="none" className="menu-list user-pages">
                  <IonItem
                    button
                    onClick={() => {
                      if (isIOSSafari || isChromeIOS) {
                        setShowIOSSheet(true);
                      } else if (hasNativePrompt) {
                        void triggerInstall();
                      } else {
                        showToast({ type: 'info', message: "Tap your browser menu (\u22EE) and select 'Add to Home screen'", cssClass: 'toast-dark-text' });
                      }
                      void handleCloseMenu();
                    }}
                    className="action-container"
                  >
                    <IonLabel className="lbl-white">
                      <IonIcon icon={addToHomeIcon} className="sidemenu-icon" />
                      <span className="sidemenu-text">Add to Home screen</span>
                    </IonLabel>
                  </IonItem>
                </IonList>
              )}

              {authState && (
                <MenuListSection
                  id="logout-button"
                  className="info-pages"
                  pages={menuRoutes.logoutPages}
                  signOut={() => {
                    handleCloseMenu().catch(() => null);
                    signOut();
                  }}
                  goToAppTour={handleResetTour}
                  onRouteChange={handleCloseMenu}
                />
              )}
            </IonContent>
          </div>
        </div>
        <CopyRight isShowVersion={true} />
        {location.pathname === '/detail' && (
          <div className="side-menu-footer">
            <IonRow>
              <IonCol>
                <IonButton
                  expand="full"
                  type="submit"
                  id="login-submit"
                  className="btn-primary submit-btn"
                  onClick={goBackAction}
                >
                  Back to the map
                </IonButton>
              </IonCol>
            </IonRow>
          </div>
        )}
      </IonMenu>
      <IOSInstallSheet isOpen={showIOSSheet} onClose={() => setShowIOSSheet(false)} browser={isChromeIOS ? 'chrome' : 'safari'} />
    </>
  );
};

export default SideMenu;
